import { useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

/**
 * useState that persists to localStorage — used to remember a list page's
 * filter selections across navigation and browser sessions.
 *
 * Mirrors the existing column-visibility/recent-searches localStorage pattern.
 * Pass a stable, unique `storageKey` (use `makeFilterKey` to scope per user +
 * page + field). When `storageKey` is empty/undefined the value is NOT persisted
 * (behaves like plain useState) — handy before the current user id is known.
 *
 * @example
 *   const fk = makeFilterKey(currentUser?.id, 'tasks')
 *   const [sortBy, setSortBy] = usePersistentState(fk('sortBy'), 'activity_date')
 */
export function usePersistentState<T>(
  storageKey: string | undefined,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (!storageKey) return defaultValue
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored !== null) return JSON.parse(stored) as T
    } catch {
      // ignore corrupt/parse errors — fall back to default
    }
    return defaultValue
  })

  // Skip persisting on the very first render (we just loaded it).
  const hydrated = useRef(false)
  useEffect(() => {
    if (!storageKey) return
    if (!hydrated.current) {
      hydrated.current = true
      return
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(value))
    } catch {
      // ignore quota/serialisation errors
    }
  }, [value, storageKey])

  return [value, setValue]
}

/**
 * Build a per-user, per-page localStorage key factory for filter state, e.g.
 * `gracklecrm_filters_<userId>_<page>_<field>`. Returns `undefined` keys when
 * there's no user id yet, so `usePersistentState` skips persistence until ready.
 */
export function makeFilterKey(userId: string | undefined | null, page: string) {
  return (field: string): string | undefined =>
    userId ? `gracklecrm_filters_${userId}_${page}_${field}` : undefined
}
